import { useMemo } from "react";
import {
  TextField,
  DatagridConfigurable,
  DateField,
  NumberField,
  FunctionField,
  useTranslate,
  RecordContextProvider,
  ChipField,
  useTheme,
  usePermissions,
} from "react-admin";
import mutationRepackingFilterList from "../../components/mutationRepackings/filter/mutationRepackingFilterList";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import InventoryRepackingExpandList from "../../components/inventoryRepackings/expand/InventoryRepackingExpandList";
import InventoryRepackingFooterList from "../../components/inventoryRepackings/footer/InventoryRepackingFooterList";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";
import ManualReportList from "../../components/reports/ManualReportList";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const InventoryRepackingList = () => {
  const { permissions } = usePermissions();
  const t = useTranslate();
  const [theme] = useTheme("light");
  const { filterDefaultValues } = useCompanyScopedListFilters({
    start_date: firstDate,
    end_date: lastDate,
    approved: 1,
  });

  const list = useMemo(
    () => (
      <ManualReportList
        title="mine.list_title.mutation_repacking"
        empty={false}
        actions={<DownloadAction />}
        filters={mutationRepackingFilterList(permissions)}
        queryOptions={{
          meta: {
            sort: [
              "mutation_repackings.type ASC",
              "mutation_repackings.code ASC",
            ],
          },
        }} // overide default sort
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date"]}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<InventoryRepackingFooterList />} />}
          bulkActionButtons={false}
          expand={<InventoryRepackingExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          <TextField source="company.alias" label="Depo" />
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <FunctionField
            label="mine.label.type"
            textAlign="center"
            render={(record) => {
              let rec = {};
              if (record.type === 1) {
                rec.type = t("mine.label.stocktake");
                rec.color = "primary";
              } else if (record.type === 2) {
                rec.type = t("mine.label.sample");
                rec.color = "success";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="type" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
          <TextField
            source="delivery_note"
            label="mine.label.delivery_note"
            emptyText="-"
          />
          <NumberField source="quantity" />
          <NumberField source="total" />
          <TextField source="note" label="mine.label.note" />
          <TextField source="creator_name" label="mine.label.created_by" />
          <DateField
            source="created_at"
            showTime
            label="mine.label.created_at"
          />
        </DatagridConfigurable>
      </ManualReportList>
    ),
    [theme, permissions, filterDefaultValues]
  );
  return list;
};

export default InventoryRepackingList;
